import React, { useEffect, useMemo, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ChevronLeft, Wallet, CreditCard, Info } from "lucide-react";
import { toast } from "sonner";

// Stripe first — that's the money-flow we control.
const PAYMENT_OPTIONS = [
  { key: "stripe", label: "Pay with card (Stripe)", primary: true },
  { key: "cashapp", label: "Cash App" },
  { key: "venmo", label: "Venmo" },
  { key: "zelle", label: "Zelle" },
  { key: "paypal", label: "PayPal" },
  { key: "in_person", label: "In-person / Pickup" },
];

const TIP_PRESETS = [0, 10, 15, 20];  // percent

// Integer-cent service fee — MUST match backend `compute_service_fee_cents`.
const serviceFeeCents = (subC) => Math.ceil((subC * 7) / 100) + 50;

export default function Checkout() {
  const { cart, refresh } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [vendors, setVendors] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tipPreset, setTipPreset] = useState(0);
  const [tipCustom, setTipCustom] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState(null); // {promotion, discount_cents}
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [loyaltyWallets, setLoyaltyWallets] = useState({}); // shop_id → wallet
  const [loyaltyPrograms, setLoyaltyPrograms] = useState({}); // shop_id → program

  const groups = useMemo(() => {
    const map = {};
    for (const it of cart.items) {
      const vid = it.product.vendor_id;
      if (!map[vid]) map[vid] = { vendor_id: vid, vendor_name: it.product.vendor_name, items: [], total: 0 };
      map[vid].items.push(it);
      map[vid].total += it.line_total;
    }
    return Object.values(map);
  }, [cart.items]);

  useEffect(() => {
    (async () => {
      const ids = groups.map((g) => g.vendor_id);
      const out = {};
      for (const id of ids) {
        try {
          const { data } = await api.get(`/vendors/${id}`);
          out[id] = data;
        } catch { /* ignore */ }
      }
      setVendors(out);
    })();
  }, [groups.map((g) => g.vendor_id).join(",")]); // eslint-disable-line

  const subtotalCents = Math.round(cart.total * 100);
  const stripe = paymentMethod === "stripe";
  const fee_c = stripe ? serviceFeeCents(subtotalCents) : 0;
  const tip_c = stripe
    ? (tipPreset > 0
        ? Math.round(subtotalCents * (tipPreset / 100))
        : Math.round((parseFloat(tipCustom || "0") || 0) * 100))
    : 0;
  const discount_c_coupon_only = couponPreview?.discount_cents || 0;
  // NOTE: total_c and discount_c (which include loyaltyPreview) are computed
  // below after loyaltyPreview is declared, to avoid TDZ ReferenceError.

  const applyCoupon = async () => {
    const c = (couponCode || "").trim();
    if (!c) return;
    setCouponBusy(true);
    setCouponError("");
    try {
      // Try each vendor group until one matches — most carts are single-vendor,
      // but we support multi-vendor carts by evaluating per group.
      let matched = null;
      for (const g of groups) {
        try {
          const { data } = await api.post("/promotions/validate", {
            shop_id: `shop_${g.vendor_id}`,
            code: c,
            subtotal_cents: Math.round(g.total * 100),
            product_ids: g.items.map((it) => it.product_id),
          });
          matched = { promotion: data.promotion, discount_cents: data.discount_cents };
          break;
        } catch (e) {
          if (e.response?.status !== 400) throw e;
          // 400 means "not this shop" or other validation reason; keep trying next.
          setCouponError(e.response?.data?.detail || "Promo not valid.");
        }
      }
      if (matched) {
        setCouponPreview(matched);
        setCouponError("");
        toast.success(`Saved $${(matched.discount_cents / 100).toFixed(2)}!`);
      } else {
        setCouponPreview(null);
      }
    } catch (e) {
      setCouponPreview(null);
      setCouponError(e.response?.data?.detail || "Couldn't validate code");
    } finally { setCouponBusy(false); }
  };

  const clearCoupon = () => {
    setCouponCode("");
    setCouponPreview(null);
    setCouponError("");
  };

  // Load loyalty wallets/programs for each vendor in the cart. Vendor-scoped
  // only — never cross-shop by design.
  useEffect(() => {
    if (!groups?.length) return;
    const w = {};
    const p = {};
    (async () => {
      for (const g of groups) {
        const shopId = `shop_${g.vendor_id}`;
        try {
          const [mw, mp] = await Promise.all([
            api.get(`/me/loyalty/${shopId}`),
            api.get(`/shops/${shopId}/loyalty`),
          ]);
          w[shopId] = mw.data.wallet;
          p[shopId] = mp.data.program;
        } catch { /* ignore */ }
      }
      setLoyaltyWallets(w);
      setLoyaltyPrograms(p);
    })();
  }, [groups?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute the loyalty discount preview locally — the server re-validates
  // on order creation. Loyalty applies to the FIRST eligible vendor only
  // (same rule as backend `preview_redemption`).
  const loyaltyPreview = useMemo(() => {
    const pts = Math.max(0, Number(redeemPoints) || 0);
    if (pts <= 0 || !groups?.length) return null;
    for (const g of groups) {
      const sid = `shop_${g.vendor_id}`;
      const prog = loyaltyPrograms[sid];
      const wal = loyaltyWallets[sid];
      if (!prog?.is_active || !wal) continue;
      if (pts < (prog.min_redeem_points || 0)) continue;
      if (pts > (wal.points || 0)) continue;
      const rate = prog.redeem_rate_cents_per_point || 1;
      const vendorSubC = Math.round(g.total * 100) - (couponPreview?.discount_cents || 0);
      const maxC = Math.floor(vendorSubC * (prog.max_redeem_pct || 50) / 100);
      const grossC = pts * rate;
      const discountC = Math.max(0, Math.min(grossC, maxC, vendorSubC));
      const consumed = rate > 0 ? Math.floor(discountC / rate) : 0;
      if (consumed > 0) return { shop_id: sid, points: consumed, discount_cents: discountC };
    }
    return null;
  }, [redeemPoints, groups, loyaltyWallets, loyaltyPrograms, couponPreview]);

  const discount_c = discount_c_coupon_only + (loyaltyPreview?.discount_cents || 0);
  const total_c = Math.max(0, subtotalCents + fee_c + tip_c - discount_c);

  const placeOrder = async () => {
    setSubmitting(true);
    try {
      const body = {
        payment_method: paymentMethod,
        notes,
        tip_cents: tip_c,
        origin_url: stripe ? window.location.origin : undefined,
        coupon_code: couponPreview ? (couponPreview.promotion.code || couponCode.trim()) : (couponCode.trim() || null),
        redeem_points: loyaltyPreview?.points || 0,
      };
      const { data } = await api.post("/orders", body);
      if (data.stripe?.url) {
        // Redirect to Stripe Checkout — Hidden Gem processes the payment.
        window.location.href = data.stripe.url;
        return;
      }
      toast.success(`Order placed — pay your vendor via ${paymentMethod}`);
      await refresh();
      navigate("/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't place order");
    } finally {
      setSubmitting(false);
    }
  };

  // On return from Stripe: poll status until confirmed.
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let done = false;
    (async () => {
      for (let i = 0; i < 8 && !done; i++) {
        try {
          const { data } = await api.get(`/orders/checkout/status/${sid}`);
          if (data.payment_status === "paid") {
            done = true;
            toast.success("Payment received!");
            await refresh();
            navigate("/orders");
            return;
          }
        } catch { /* ignore */ }
        await new Promise((r) => setTimeout(r, 1500));
      }
    })();
    return () => { done = true; };
  }, [params, navigate, refresh]);

  if (cart.items.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 text-center" data-testid="checkout-empty">
        <h1 className="text-3xl font-black tracking-tighter">Your bag is empty</h1>
        <Button onClick={() => navigate("/feed")} className="mt-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white px-8 h-12 glow-pink">
          Find something
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pt-5 pb-40 space-y-5" data-testid="checkout-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm text-zinc-400 hover:text-white"
        data-testid="checkout-back-btn"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Checkout</h1>
        <p className="text-zinc-400 text-sm mt-1">
          Pay through Hidden Gem — your vendor gets paid automatically.
        </p>
      </div>

      {/* Vendor breakdown */}
      <div className="space-y-3">
        {groups.map((g) => {
          const vendorInfo = vendors[g.vendor_id];
          const handles = vendorInfo?.vendor_profile?.payment_handles || {};
          return (
            <div key={g.vendor_id} className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid={`checkout-vendor-${g.vendor_id}`}>
              <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">@{g.vendor_name}</p>
              {cart.locations?.[g.vendor_id]?.label && (
                <p className="text-xs text-zinc-400 mt-1" data-testid={`checkout-location-${g.vendor_id}`}>
                  Location: <span className="text-white font-semibold">{cart.locations[g.vendor_id].label}</span>
                </p>
              )}
              <div className="mt-3 space-y-2">
                {g.items.map((it) => {
                  const snap = it.snapshot || null;
                  const sel = it.selection || null;
                  const hasCustom = Boolean(sel && (sel.variation_name || (sel.ingredients || []).length || (sel.modifiers || []).length || sel.special_instructions));
                  return (
                    <div key={it.line_id || it.product_id} className="flex items-start gap-3 text-sm" data-testid={`checkout-line-${it.line_id || it.product_id}`}>
                      <div className="w-10 h-10 rounded-lg bg-zinc-900 overflow-hidden shrink-0">
                        {it.product.image_paths?.[0] && (
                          <img src={fileUrl(it.product.image_paths[0])} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{it.product.name} × {it.quantity}</div>
                        {hasCustom && (
                          <div className="mt-0.5 text-[11px] text-zinc-400 space-y-0.5">
                            {snap?.variation_label && <div className="truncate">Size: {snap.variation_label}</div>}
                            {(snap?.modifier_labels || []).length > 0 && (
                              <div className="truncate">+ {(snap.modifier_labels || []).join(", ")}</div>
                            )}
                            {(snap?.ingredient_notes || []).length > 0 && (
                              <div className="truncate text-amber-300/80">{(snap.ingredient_notes || []).join(" · ")}</div>
                            )}
                            {snap?.special_instructions && (
                              <div className="italic text-pink-300 truncate">"{snap.special_instructions}"</div>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="font-bold whitespace-nowrap">${it.line_total.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
                <span className="text-zinc-400 text-sm">Subtotal</span>
                <span className="text-xl font-black">${g.total.toFixed(2)}</span>
              </div>
              {!stripe && handles && Object.keys(handles).length > 0 && (
                <div className="mt-3 text-xs text-zinc-400 space-y-1">
                  {PAYMENT_OPTIONS.map((opt) =>
                    handles[opt.key] ? (
                      <div key={opt.key} className="flex justify-between">
                        <span>{opt.label}</span>
                        <span className="text-pink-300 font-bold">{handles[opt.key]}</span>
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Promo code */}
      <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="checkout-coupon-card">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-amber-400" />
          <p className="text-sm font-bold uppercase tracking-widest text-amber-300">Promo code</p>
        </div>
        {couponPreview ? (
          <div className="flex items-center gap-2 rounded-2xl bg-emerald-500/10 border border-emerald-400/40 p-3" data-testid="checkout-coupon-applied">
            <div className="flex-1">
              <p className="text-sm font-black text-emerald-100">{couponPreview.promotion.name}</p>
              <p className="text-xs text-emerald-200">Code {couponPreview.promotion.code} · You saved ${(couponPreview.discount_cents / 100).toFixed(2)}</p>
            </div>
            <button onClick={clearCoupon} className="text-xs text-emerald-100 underline" data-testid="checkout-coupon-clear">Remove</button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                maxLength={32}
                className="flex-1 h-11 px-4 rounded-2xl bg-white/5 border border-white/10 text-white uppercase tracking-widest text-sm outline-none focus:border-pink-400"
                data-testid="checkout-coupon-input"
              />
              <Button onClick={applyCoupon} disabled={couponBusy || !couponCode.trim()} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold h-11 px-5" data-testid="checkout-coupon-apply">
                Apply
              </Button>
            </div>
            {couponError && <p className="text-xs text-rose-300" data-testid="checkout-coupon-error">{couponError}</p>}
          </div>
        )}
      </div>

      {/* Loyalty redemption — vendor-specific */}
      {Object.values(loyaltyWallets).some((w) => (w?.points || 0) > 0) && (
        <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="checkout-loyalty-card">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-amber-400" />
            <p className="text-sm font-bold uppercase tracking-widest text-amber-300">Loyalty points</p>
          </div>
          {groups.map((g) => {
            const sid = `shop_${g.vendor_id}`;
            const w = loyaltyWallets[sid];
            const p = loyaltyPrograms[sid];
            if (!w || !p?.is_active || (w.points || 0) <= 0) return null;
            return (
              <div key={sid} className="rounded-2xl bg-amber-500/5 border border-amber-400/30 p-3 mb-2" data-testid={`checkout-loyalty-shop-${sid}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-amber-100 font-bold">
                    {g.vendor_name} · <span className="font-black">{w.points.toLocaleString()}</span> pts available
                  </p>
                  <p className="text-[10px] text-amber-200/80">Min {p.min_redeem_points} · worth {p.redeem_rate_cents_per_point}¢/pt</p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    max={w.points}
                    value={redeemPoints}
                    onChange={(e) => setRedeemPoints(Math.max(0, Math.min(w.points, Number(e.target.value) || 0)))}
                    placeholder="0"
                    className="flex-1 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-amber-400"
                    data-testid="checkout-loyalty-input"
                  />
                  <button
                    onClick={() => setRedeemPoints(Math.min(w.points, Math.max(w.points, p.min_redeem_points)))}
                    className="h-10 px-3 rounded-xl bg-amber-500/15 border border-amber-400/40 text-amber-100 text-xs font-bold"
                    data-testid="checkout-loyalty-max"
                  >
                    Use max
                  </button>
                  {redeemPoints > 0 && (
                    <button
                      onClick={() => setRedeemPoints(0)}
                      className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-zinc-200 text-xs font-bold"
                      data-testid="checkout-loyalty-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {loyaltyPreview?.shop_id === sid && (
                  <p className="text-xs text-emerald-300 mt-2" data-testid="checkout-loyalty-preview">
                    −${(loyaltyPreview.discount_cents / 100).toFixed(2)} applied ({loyaltyPreview.points} pts)
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Payment method picker */}
      <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-pink-400" />
          <p className="text-sm font-bold uppercase tracking-widest text-pink-300">How will you pay?</p>
        </div>
        <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
          {PAYMENT_OPTIONS.map((opt) => (
            <label
              key={opt.key}
              htmlFor={`pm-${opt.key}`}
              className={`flex items-center gap-3 p-3 rounded-2xl cursor-pointer border transition-colors ${
                paymentMethod === opt.key
                  ? "bg-pink-500/10 border-pink-400/40"
                  : "bg-white/[0.02] border-white/10 hover:bg-white/5"
              }`}
              data-testid={`payment-option-${opt.key}`}
            >
              <RadioGroupItem id={`pm-${opt.key}`} value={opt.key} />
              <span className="font-semibold flex-1">{opt.label}</span>
              {opt.primary && <CreditCard className="w-4 h-4 text-cyan-300" />}
            </label>
          ))}
        </RadioGroup>
      </div>

      {/* Tip — Stripe only. 0% commission on the tip itself. */}
      {stripe && (
        <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="checkout-tip">
          <p className="text-sm font-bold uppercase tracking-widest text-cyan-300 mb-3">Add a tip? (Optional)</p>
          <div className="grid grid-cols-4 gap-2">
            {TIP_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => { setTipPreset(p); setTipCustom(""); }}
                className={`h-10 rounded-full text-sm font-black transition-colors ${
                  tipPreset === p && !tipCustom
                    ? "bg-cyan-400 text-black"
                    : "bg-white/5 border border-white/10 text-white hover:bg-white/10"
                }`}
                data-testid={`tip-preset-${p}`}
              >
                {p === 0 ? "No tip" : `${p}%`}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Label htmlFor="tip-custom" className="text-zinc-400 text-xs">Or a custom amount ($)</Label>
            <input
              id="tip-custom"
              type="number"
              step="0.01"
              min="0"
              value={tipCustom}
              onChange={(e) => { setTipCustom(e.target.value); setTipPreset(-1); }}
              placeholder="0.00"
              className="mt-1 w-full h-11 rounded-2xl bg-white/5 border border-white/10 text-white px-4"
              data-testid="tip-custom-input"
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-2 inline-flex items-center gap-1">
            <Info className="w-3 h-3" /> 100% of the tip goes to your vendor. Hidden Gem takes 0%.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-zinc-300">Notes for vendor (optional)</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Pickup time, dietary notes, etc."
          className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[72px]"
          data-testid="checkout-notes"
        />
      </div>

      {/* Itemized total */}
      <div className="rounded-3xl bg-gradient-to-br from-pink-500/15 to-transparent border border-pink-400/20 p-5 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-zinc-300">Subtotal</span>
          <span className="font-bold tabular-nums" data-testid="checkout-subtotal">${(subtotalCents / 100).toFixed(2)}</span>
        </div>
        {stripe && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-300">Hidden Gem Service Fee</span>
              <span className="font-bold tabular-nums" data-testid="checkout-service-fee">${(fee_c / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-300">Tip</span>
              <span className="font-bold tabular-nums" data-testid="checkout-tip-amt">${(tip_c / 100).toFixed(2)}</span>
            </div>
          </>
        )}
        {couponPreview && (couponPreview.discount_cents || 0) > 0 && (
          <div className="flex justify-between text-sm" data-testid="checkout-discount-row">
            <span className="text-emerald-200">Promo · {couponPreview.promotion.code}</span>
            <span className="font-bold tabular-nums text-emerald-300">−${((couponPreview.discount_cents || 0) / 100).toFixed(2)}</span>
          </div>
        )}
        {loyaltyPreview && (loyaltyPreview.discount_cents || 0) > 0 && (
          <div className="flex justify-between text-sm" data-testid="checkout-loyalty-row">
            <span className="text-amber-200">Loyalty · {loyaltyPreview.points} pts</span>
            <span className="font-bold tabular-nums text-amber-300">−${((loyaltyPreview.discount_cents || 0) / 100).toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-white/10">
          <span className="text-zinc-100 font-bold">Total</span>
          <span className="text-3xl font-black tabular-nums" data-testid="checkout-total">${(total_c / 100).toFixed(2)}</span>
        </div>
        <button
          type="button"
          onClick={placeOrder}
          disabled={submitting}
          className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 disabled:opacity-50 text-white font-bold glow-pink transition-colors mt-2"
          data-testid="place-order-btn"
        >
          {submitting ? "Redirecting to Stripe…" : stripe ? `Pay $${(total_c / 100).toFixed(2)}` : "Place order"}
        </button>
        {!stripe && (
          <p className="text-xs text-zinc-500 mt-3 text-center">
            You'll send payment directly to your vendor. They'll mark the order paid.
          </p>
        )}
      </div>
    </div>
  );
}
